import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Table, Button, message, Select, Tag, Tooltip } from "antd";
import { ReloadOutlined, PrinterOutlined, ShopOutlined, DollarOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { PageHeader, StatCard, T, cardStyle, Money } from "../../../shared/ui/PageShell";
import DateRangeFilter from "../../../shared/ui/DateRangeFilter";
import { formatRangeParams } from "../../../shared/lib/dateFilterPresets";
import { printFarmerSettlementStatement } from "../lib/printFarmerSettlement";
import { fetchExpenseTypes } from "../lib/expenseTypes";
import { formatSettlementNo } from "../../../shared/lib/documentNumbers";
import { vendorFinanceFromConsignment } from "../lib/vendorFinance";
import { getPrintedByInfo } from "../../../shared/lib/session";
import useTablePagination from "../../../shared/ui/useTablePagination";

const FarmerSettlementArchives = ({ embedded = false } = {}) => {
  const { t, i18n } = useTranslation();
  const { pagination } = useTablePagination();
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vendorFilter, setVendorFilter] = useState(null);
  const [range, setRange] = useState([null, null]);
  const [printingId, setPrintingId] = useState(null);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const mainCode = systemConfig?.currency_code || "IQD";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = formatRangeParams(range, {
        withTime: false,
        fromKey: "start_date",
        toKey: "end_date",
      });
      if (vendorFilter) params.vendor_id = vendorFilter;
      const res = await apiClient.get("/vendor-settlement-archives", { params });
      setRows(Array.isArray(res.data?.archives) ? res.data.archives : []);
    } catch (e) {
      message.error(e?.response?.data?.message || t("loadError", "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [vendorFilter, range, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiClient.get("/vendors").then((r) => setVendors(Array.isArray(r.data) ? r.data : [])).catch(() => setVendors([]));
    fetchExpenseTypes().then(setExpenseTypes).catch(() => setExpenseTypes([]));
    fetchSystemConfig().then(setSystemConfig).catch(() => {});
  }, []);

  const reprint = async (record) => {
    setPrintingId(record.id);
    try {
      const res = await apiClient.get(`/vendor-settlement-archives/${record.id}`);
      const archive = res.data;
      const snapshot = archive.snapshot || {};
      await printFarmerSettlementStatement({
        consignment: snapshot,
        vendor: {
          id: archive.vendor_id,
          name: archive.vendor_name || snapshot.vendor?.name,
          phone: archive.vendor_phone || snapshot.vendor?.phone,
        },
        systemConfig,
        language: i18n.language,
        t,
        dateFrom: archive.date_from || snapshot.date_from,
        dateTo: archive.date_to || snapshot.date_to,
        settledAt: archive.settled_at,
        archiveId: archive.id,
        expenseTypes,
        previousDebt: snapshot.previous_debt,
        totalDebt: snapshot.total_debt,
        documentNo: archive.document_no || formatSettlementNo(archive.id, archive.settled_at),
        printedBy: getPrintedByInfo(),
      });
    } catch (e) {
      message.error(e?.response?.data?.message || t("loadError", "Failed to load"));
    } finally {
      setPrintingId(null);
    }
  };

  const columns = [
    {
      title: <b>{t("docNo", "No.")}</b>, key: "document_no", width: 140,
      render: (_, r) => (
        <span style={{ fontWeight: 700, fontFamily: "monospace", fontSize: 12 }}>
          {r.document_no || formatSettlementNo(r.id, r.settled_at)}
        </span>
      ),
    },
    {
      title: <b>{t("vendorsColName", "Farmer")}</b>, key: "vendor", width: 160,
      render: (_, r) => (
        <Link to={`/vendors/${r.vendor_id}`} style={{ fontWeight: 600 }}>
          {r.vendor_name || `#${r.vendor_id}`}
        </Link>
      ),
    },
    {
      title: <b>{t("vendorArchivePeriod", "Period")}</b>, key: "period", width: 170,
      render: (_, r) => (
        <span style={{ fontSize: 12, color: T.sub, whiteSpace: "nowrap" }}>
          {(r.date_from || "—") + " → " + (r.date_to || "—")}
        </span>
      ),
    },
    {
      title: <b>{t("vendorGross", "Gross")}</b>, dataIndex: "gross", key: "gross", align: "right", width: 150,
      render: (v) => <Money>{formatCurrency(v, mainCode)}</Money>,
    },
    {
      title: <b>{t("batchExpenses", "Expenses")}</b>, dataIndex: "expenses_total", key: "exp", align: "right", width: 150,
      render: (v) => <Money color="#d97706">−{formatCurrency(v, mainCode)}</Money>,
    },
    {
      title: <b>{t("vendorNet", "Net")}</b>, key: "net", align: "right", width: 150,
      render: (_, r) => {
        // Product − expenses only (ignore archive commission — company profit, not farmer total).
        const finance = vendorFinanceFromConsignment({
          total_gross: r.gross,
          total_expenses: r.expenses_total,
          total_net_owed: r.net,
          total_commission: 0,
          total_paid: r.amount_paid,
        });
        return <Money strong color="#0891b2">{formatCurrency(finance.netOwed, mainCode)}</Money>;
      },
    },
    {
      title: <b>{t("vendorAlreadyPaid", "Paid")}</b>, dataIndex: "amount_paid", key: "paid", align: "right", width: 150,
      render: (v) => (
        <Tag color="green" style={{ margin: 0 }}>
          <Money>{formatCurrency(v, mainCode)}</Money>
        </Tag>
      ),
    },
    {
      title: <b>{t("date", "Date")}</b>, dataIndex: "settled_at", key: "settled_at", width: 140,
      render: (v) => <span style={{ fontSize: 12, color: T.muted, whiteSpace: "nowrap" }}>{v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}</span>,
    },
    {
      title: <b>{t("clearedBy", "Cleared by")}</b>, dataIndex: "created_by_name", key: "cleared_by", width: 140,
      render: (v) => <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{v || "—"}</span>,
    },
    {
      title: "", key: "actions", width: 160, align: "center", fixed: "right",
      render: (_, r) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
          <Tooltip title={t("view", "View")}>
            <Link to={`/farmer-settlement-archives/${r.id}`}>
              <Button size="small" icon={<EyeOutlined />} style={{ borderRadius: 7 }} />
            </Link>
          </Tooltip>
          <Button
            size="small"
            icon={<PrinterOutlined />}
            loading={printingId === r.id}
            style={{ borderRadius: 7 }}
            onClick={() => reprint(r)}
          >
            {t("print", "Print")}
          </Button>
        </div>
      ),
    },
  ];

  const totalNet = rows.reduce((a, r) => a + (parseFloat(r.net) || 0), 0);

  return (
    <div style={{ padding: embedded ? 0 : 24, background: T.bg, minHeight: embedded ? "auto" : "100%" }}>
      {!embedded && (
        <PageHeader
          breadcrumb={[t("vendorsMenu", "Farmers"), t("farmerArchivesTitle", "Settlement Archives")]}
          title={t("farmerArchivesTitle", "Settlement Archives")}
          subtitle={t("farmerArchivesHint", "Cleared farmer statements — filter and reprint anytime")}
          action={
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 9 }}>
              {t("currencyTotalsRefresh", "Refresh")}
            </Button>
          }
        />
      )}
      {embedded && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 9 }}>
            {t("currencyTotalsRefresh", "Refresh")}
          </Button>
        </div>
      )}

      <div className="velan-stats-row">
        <StatCard icon={<ShopOutlined style={{ color: "#2563eb" }} />} label={t("count", "Count")} value={rows.length} iconBg="#dbeafe" />
        <StatCard icon={<DollarOutlined style={{ color: "#0891b2" }} />} label={t("vendorNet", "Net")} value={formatCurrency(totalNet, mainCode)} iconBg="#cffafe" valueColor="#0891b2" />
      </div>

      <div style={{ ...cardStyle, marginBottom: 16, padding: "12px 16px", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <Select
          allowClear showSearch optionFilterProp="label"
          placeholder={t("vendorsColName", "Farmer")}
          style={{ minWidth: 220 }}
          value={vendorFilter}
          onChange={(v) => setVendorFilter(v ?? null)}
          options={vendors.map((v) => ({ value: v.id, label: v.name }))}
        />
        <DateRangeFilter
          value={range}
          onChange={setRange}
          showTime={false}
          allowClear
        />
      </div>

      <div style={cardStyle}>
        <Table
          size="small"
          loading={loading}
          dataSource={rows.map((r) => ({ ...r, key: r.id }))}
          columns={columns}
          pagination={pagination}
          scroll={{ x: 1480 }}
        />
      </div>
    </div>
  );
};

export default FarmerSettlementArchives;
